import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import Modal from '@/components/ui/Modal';
import { selectCartCount, selectCartMode, selectCartModeChosen, setMode } from '@/features/cart/cartSlice';
import { useAllowedModes } from '@/features/checkout/useAllowedModes';
import type { OrderMode } from '@/types';

const ignoreClose = () => {};

/** La fenetre ne contient que des boutons : Tab tourne entre eux au lieu de
 *  partir sur la carte, derriere le voile. */
function keepTabInside(e: KeyboardEvent<HTMLDivElement>) {
  if (e.key !== 'Tab') return;
  const buttons = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('button'));
  if (buttons.length === 0) return;
  const first = buttons[0];
  const last = buttons[buttons.length - 1];
  if (e.shiftKey && (document.activeElement === first || document.activeElement === e.currentTarget)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

/**
 * SC-1092 — « A emporter ou Livraison ? » se demande AVANT de commander.
 *
 * Un plat peut avoir un prix par mode. La carte s'ouvrait aux prix « A
 * emporter » sans rien demander : le client composait son panier sur ces prix,
 * choisissait « Livraison » au panier ou au paiement, et le total changeait sous
 * ses yeux — un litige. Quand le restaurant propose les deux modes, la carte
 * pose donc la question d'abord et ne se ferme pas sans reponse ; un bandeau
 * rappelle ensuite le mode retenu et permet d'en changer.
 *
 * Un seul mode possible : rien a demander, rien n'est rendu. La liste vient de
 * `useAllowedModes`, comme au panier et au paiement (SC-570) : tant que les deux
 * backends n'ont pas repondu, la livraison n'est pas proposee — donc pas de
 * question non plus.
 *
 * Le preflight de Tailwind est coupe sur ce site et les couleurs sont des
 * `var()` nues : un `bg-mint/20` n'y peint rien, et une bordure de <div> n'existe
 * qu'avec `border-solid`. D'ou les classes explicites ci-dessous.
 */
export default function OrderModeChooser({ restaurantUuid }: { restaurantUuid: string }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { modes, resolved } = useAllowedModes(restaurantUuid);
  const mode = useAppSelector(selectCartMode(restaurantUuid));
  const chosen = useAppSelector(selectCartModeChosen(restaurantUuid));
  const cartCount = useAppSelector(selectCartCount(restaurantUuid));
  const [changing, setChanging] = useState(false);
  const changeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogBodyRef = useRef<HTMLDivElement>(null);
  const currentModeRef = useRef<HTMLButtonElement>(null);

  const offersBoth = resolved && modes.includes('takeaway') && modes.includes('delivery');
  const open = offersBoth && (!chosen || changing);

  // Le focus entre dans la fenetre a l'ouverture et revient sur « Changer »
  // quand elle se referme. Premiere fois : sur le corps de la fenetre, pas sur
  // un bouton — un anneau de focus sur « A emporter » se lirait comme un choix
  // deja fait. Rouverte : sur le mode retenu, deja mis en avant.
  useEffect(() => {
    if (!open) return;
    (currentModeRef.current ?? dialogBodyRef.current)?.focus();
  }, [open]);
  const wasChanging = useRef(false);
  useEffect(() => {
    if (wasChanging.current && !changing) changeButtonRef.current?.focus();
    wasChanging.current = changing;
  }, [changing]);

  if (!offersBoth) return null;

  const label = (m: OrderMode) => t(m === 'delivery' ? 'order.modeDelivery' : 'order.modeTakeaway');
  const pick = (m: OrderMode) => {
    dispatch(setMode({ restaurantUuid, mode: m }));
    setChanging(false);
  };

  return (
    <>
      {chosen && (
        <div
          data-testid="order-mode-strip"
          className="bg-cream border-0 border-b-2 border-solid border-ink px-4 py-1 font-mono text-sm text-ink flex items-center justify-between gap-3"
        >
          <span>{t('menu.orderMode.current', { mode: label(mode) })}</span>
          <button
            ref={changeButtonRef}
            type="button"
            onClick={() => setChanging(true)}
            className="bg-paper border-2 border-solid border-ink rounded-btn-sm px-3 min-h-[44px] font-display text-sm text-ink shadow-stamp-sm shrink-0"
          >
            {t('menu.orderMode.change')}
          </button>
        </div>
      )}
      {/* Premiere fois : ni le fond ni Echap ne ferment — la question est
          tout l'objet de la fenetre. Rouverte depuis le bandeau, elle se ferme
          normalement et garde le mode deja retenu. */}
      <Modal
        open={open}
        onClose={chosen ? () => setChanging(false) : ignoreClose}
        ariaLabel={t('menu.orderMode.title')}
        dismissOnBackdrop={chosen}
      >
        <div
          ref={dialogBodyRef}
          tabIndex={-1}
          data-testid="order-mode-chooser"
          onKeyDown={keepTabInside}
          className="px-5 py-6 sm:px-8 sm:py-7 flex flex-col gap-4 text-center outline-none"
        >
          <h2 className="font-display text-2xl text-ink m-0">{t('menu.orderMode.title')}</h2>
          <p className="font-mono text-sm text-ink/70 m-0">{t('menu.orderMode.hint')}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            {modes.map((m) => {
              const current = chosen && mode === m;
              return (
                <button
                  key={m}
                  ref={current ? currentModeRef : undefined}
                  type="button"
                  onClick={() => pick(m)}
                  aria-pressed={chosen ? current : undefined}
                  className={`flex-1 py-4 border-2 border-solid border-ink rounded-btn font-display text-lg shadow-stamp-sm ${
                    current ? 'bg-mint text-[var(--on-primary)]' : 'bg-paper text-ink'
                  }`}
                >
                  {current && <span aria-hidden="true">✓ </span>}
                  {label(m)}
                </button>
              );
            })}
          </div>
          {chosen && cartCount > 0 && (
            <p className="font-mono text-sm text-ink/80 m-0">{t('menu.orderMode.cartRepriced')}</p>
          )}
          {chosen && (
            <button
              type="button"
              onClick={() => setChanging(false)}
              className="bg-paper border-2 border-solid border-ink rounded-btn py-2 min-h-[44px] font-display text-sm text-ink"
            >
              {t('common.cancel')}
            </button>
          )}
        </div>
      </Modal>
    </>
  );
}
